import { useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Bar, BarChart, CartesianGrid, Cell, ReferenceLine, Tooltip, XAxis, YAxis } from "recharts";

import { cn } from "@/lib/utils";

import type { ApiParams, Page } from "../../api";
import { FlagChip, FLAGS } from "../../chips";
import { OptionLink } from "../../links";
import { TabPage } from "../../PageFrame";
import type { SourcingTab } from "../../routes";
import { CHART, criteriaColour, TOOLTIP_STYLE } from "../charts";
import { Question, type CriteriaRankingRow } from "../criteria_ranking/Page";
import {
  MIN_STANDARD,
  MUTED_ROW,
  PagedTable,
  sortParams,
  usePage,
  usePer,
  useTableSort,
} from "../kit";
import { pageFilter, pageFilterList } from "../params";

export interface HeatmapCriterion {
  criterion: string;
  question: string | null;
  in_catalog?: boolean;
  off_catalog?: boolean;
}

export interface HeatmapOption {
  option_id: string;
  option_name: string;
  item_id: string;
  rank_n: number;
  dropped: boolean;
  rating?: number | null;
}

export interface HeatmapCell {
  option_id: string;
  criterion: string;
  rating: number | null;
  comment: string | null;
  sample?: string | null;
  evaluator?: string | null;
  rated_n?: number;
}

export interface HeatmapData {
  criteria: HeatmapCriterion[];
  options: HeatmapOption[];
  cells: HeatmapCell[];
}

export interface CriteriaChartRow {
  option_id: string;
  option_name: string;
  [criterion: string]: string | number | null;
}

export function pagePhase(search: Record<string, unknown>): string | null {
  return pageFilter(search, "criteria_phase");
}

const isOffCatalog = (criterion: HeatmapCriterion) =>
  criterion.off_catalog === true || criterion.in_catalog === false;

/**
 * v1.7 (Ioan 3 Nov): with samples picked, each picked option x sample is its own bar group, "Option – Sample", so
 * two pouches of one supplier sit side by side. The group keeps its option's Overall score and rank for the order.
 */
export function splitBySample(data: HeatmapData): HeatmapData {
  const byId = new Map(data.options.map((option) => [option.option_id, option]));
  const groups = new Map<string, HeatmapOption>();
  const cells: HeatmapCell[] = [];
  for (const cell of data.cells) {
    const option = byId.get(cell.option_id);
    if (!option || cell.sample == null) continue;
    const id = `${cell.option_id}\u0000${cell.sample}`;
    if (!groups.has(id)) groups.set(id, { ...option, option_id: id, option_name: `${option.option_name} \u2013 ${cell.sample}` });
    cells.push({ ...cell, option_id: id });
  }
  return { criteria: data.criteria, options: [...groups.values()], cells };
}

/** Options left to right by Overall score, best first, unrated last (Ioan, 30 Oct: no control on the page). */
export function byOverall(options: HeatmapOption[]): HeatmapOption[] {
  return [...options].sort((a, b) =>
    (b.rating ?? -1) - (a.rating ?? -1) || a.rank_n - b.rank_n || a.option_name.localeCompare(b.option_name));
}

/** One cell per option x criterion: the mean of its rated records (Ioan 30 Oct, "Average all rows"; API crit_avg). */
export function averageCells(cells: HeatmapCell[]): HeatmapCell[] {
  const groups = new Map<string, HeatmapCell[]>();
  for (const cell of cells) {
    const key = `${cell.option_id}\u0000${cell.criterion}`;
    groups.set(key, [...(groups.get(key) ?? []), cell]);
  }
  return [...groups.values()].map((group) => {
    const rated = group.filter((cell) => cell.rating != null).map((cell) => cell.rating as number);
    return {
      option_id: group[0].option_id,
      criterion: group[0].criterion,
      rating: rated.length ? Math.round((rated.reduce((sum, value) => sum + value, 0) / rated.length) * 100) / 100 : null,
      comment: group.length === 1 ? group[0].comment : null,
      rated_n: rated.length,
    };
  });
}

/** Only criteria with a rating on a shown option: an all-blank criterion would leave an empty slot in every group. */
export function ratedCriteria(data: HeatmapData): HeatmapCriterion[] {
  const shown = new Set(data.options.map((option) => option.option_id));
  const rated = new Set(
    averageCells(data.cells).filter((cell) => cell.rating != null && shown.has(cell.option_id)).map((cell) => cell.criterion),
  );
  return data.criteria.filter((criterion) => rated.has(criterion.criterion));
}

/** Every criterion with an answer on a shown option, including CLARIFY comments without a rating. */
export function answeredCriteria(data: HeatmapData): HeatmapCriterion[] {
  const shown = new Set(data.options.map((option) => option.option_id));
  const answered = new Set(
    averageCells(data.cells).filter((cell) => shown.has(cell.option_id)).map((cell) => cell.criterion),
  );
  return data.criteria.filter((criterion) => answered.has(criterion.criterion));
}

export function chartRows(data: HeatmapData): CriteriaChartRow[] {
  const ratings = new Map(
    data.cells.map((cell) => [`${cell.option_id}\u0000${cell.criterion}`, cell.rating]),
  );
  return byOverall(data.options).map((option) => {
    const row: CriteriaChartRow = {
      option_id: option.option_id,
      option_name: option.option_name,
    };
    for (const criterion of data.criteria) {
      row[criterion.criterion] = ratings.get(`${option.option_id}\u0000${criterion.criterion}`) ?? null;
    }
    return row;
  });
}

const CHART_HEIGHT = 480;
export const CLARIFY_STUB = 1;
const isClarify = (cell: HeatmapCell) => cell.rating == null;

/** One option's bars, packed: rated and CLARIFY answers centred in its group. */
export interface PackedRow {
  option_id: string;
  option_name: string;
  ratings: (number | null)[];
  criteria: (string | null)[];
  clarify: boolean[];
  rated_n: number[];
}

/** Packs each option's answered criteria into adjacent slots, so a sparse option draws no gaps (Ioan, 30 Oct). */
export function packedRows(data: HeatmapData, criteria: HeatmapCriterion[]): { rows: PackedRow[]; slots: number } {
  const order = new Map(criteria.map((criterion, index) => [criterion.criterion, index]));
  const answered = new Map<string, HeatmapCell[]>();
  for (const cell of averageCells(data.cells)) {
    if (!order.has(cell.criterion)) continue;
    answered.set(cell.option_id, [...(answered.get(cell.option_id) ?? []), cell]);
  }
  const slots = Math.max(1, ...[...answered.values()].map((cells) => cells.length));
  const rows = byOverall(data.options).map((option) => {
    const cells = [...(answered.get(option.option_id) ?? [])].sort(
      (a, b) => (order.get(a.criterion) ?? 0) - (order.get(b.criterion) ?? 0),
    );
    const pad = Math.floor((slots - cells.length) / 2);
    const row: PackedRow = {
      option_id: option.option_id,
      option_name: option.option_name,
      ratings: Array(slots).fill(null),
      criteria: Array(slots).fill(null),
      clarify: Array(slots).fill(false),
      rated_n: Array(slots).fill(0),
    };
    cells.forEach((cell, index) => {
      row.ratings[pad + index] = cell.rating ?? CLARIFY_STUB;
      row.criteria[pad + index] = cell.criterion;
      row.clarify[pad + index] = isClarify(cell);
      row.rated_n[pad + index] = cell.rated_n ?? 0;
    });
    return row;
  });
  return { rows, slots };
}

/** One option's group: slim bars, so a full page of options fits before the chart scrolls sideways. */
const groupWidth = (slots: number) => Math.max(44, slots * 7 + 14);
const shortName = (name: string) => (name.length > 20 ? `${name.slice(0, 19)}…` : name);

/** The width the chart's box really has, so the chart fills the page instead of a fixed 900px. */
function useBoxWidth(fallback: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const box = ref.current;
    if (!box || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    observer.observe(box);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

const criterionLabel = (criterion: HeatmapCriterion) => `${criterion.criterion}${isOffCatalog(criterion) ? "*" : ""}`;

function ChartLegend({ criteria, clarify }: { criteria: HeatmapCriterion[]; clarify: boolean }) {
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-ks-ink">
      {criteria.map((criterion, index) => (
        <li key={criterion.criterion} className="flex items-center gap-1.5" title={criterion.question ?? undefined}>
          <span aria-hidden="true" className="inline-block size-2.5 rounded-[3px]" style={{ background: criteriaColour(index) }} />
          {criterionLabel(criterion)}
        </li>
      ))}
      {clarify ? (
        <li className="flex items-center gap-1.5">
          <span aria-hidden="true" className="inline-block size-2.5 rounded-[3px] border border-dashed border-ks-grey-1 bg-ks-grey-4" />
          Clarify (no rating)
        </li>
      ) : null}
      <li className="flex items-center gap-1.5 text-ks-grey-1">
        <i aria-hidden="true" className="inline-block w-4 border-t-2 border-dashed border-ks-ink" />
        Min standard 7.0
      </li>
    </ul>
  );
}

export function ChartTooltip({ active, payload, colours, comments }: {
  active?: boolean;
  payload?: { payload: PackedRow }[];
  colours: Map<string, string>;
  comments: Map<string, string | null>;
}) {
  const row = active ? payload?.[0]?.payload : undefined;
  if (!row) return null;
  return (
    <div style={TOOLTIP_STYLE} className="max-w-80 px-3 py-2">
      <p className="mb-1 font-bold">{row.option_name}</p>
      <ul className="flex flex-col gap-0.5">
        {row.criteria.map((criterion, index) => {
          if (!criterion) return null;
          const comment = comments.get(`${row.option_id}\u0000${criterion}`);
          const suffix = row.rated_n?.[index] > 1
            ? ` · avg of ${row.rated_n[index]} ratings`
            : (comment ? ` · ${comment}` : "");
          return (
            <li key={criterion} className="flex items-baseline gap-1.5">
              <span aria-hidden="true" className="inline-block size-2 shrink-0 rounded-[2px]" style={{ background: row.clarify[index] ? CHART.grey4 : colours.get(criterion) }} />
              <span>{criterion}: <b>{row.clarify[index] ? "Clarify" : row.ratings[index]}</b>{suffix}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function chartCellProps(row: PackedRow, slot: number, colours: Map<string, string>) {
  if (row.clarify[slot]) {
    return { fill: CHART.grey4, stroke: CHART.grey1, strokeDasharray: "2 2" };
  }
  const criterion = row.criteria[slot];
  return { fill: criterion ? colours.get(criterion) : "transparent" };
}

/** `initialWidth` is the width before the box is measured (a static render never measures). */
export function CriteriaChart({ data, initialWidth = 960 }: { data: HeatmapData; initialWidth?: number }) {
  const [box, available] = useBoxWidth(initialWidth);
  const rated = ratedCriteria(data);
  const answered = answeredCriteria(data);
  if (answered.length === 0 || data.options.length === 0) {
    return <p>No criteria at this phase yet.</p>;
  }
  const { rows, slots } = packedRows(data, answered);
  const colours = new Map(rated.map((criterion, index) => [criterion.criterion, criteriaColour(index)]));
  const cells = averageCells(data.cells);
  const clarifyN = cells.filter(isClarify).length;
  const comments = new Map(
    cells.map((cell) => [`${cell.option_id}\u0000${cell.criterion}`, cell.comment]),
  );
  const width = Math.max(available, rows.length * groupWidth(slots) + 60);
  const tilt = width / rows.length < 110;
  const legend = rated.map(criterionLabel).join(", ");
  return (
    <div className="flex flex-col gap-4" role="img" aria-label={`Criteria ratings: ${legend}${clarifyN ? `, ${clarifyN} to clarify` : ""}. Min Standard 7.`}>
      <ChartLegend criteria={rated} clarify={clarifyN > 0} />
      <div ref={box} className="overflow-x-auto">
        <BarChart
          width={width}
          height={CHART_HEIGHT}
          data={rows}
          margin={{ top: 8, right: 12, bottom: 4, left: 0 }}
          barGap={1}
          barCategoryGap="14%"
        >
          <CartesianGrid vertical={false} stroke={CHART.grey4} strokeDasharray="3 3" />
          <XAxis
            dataKey="option_name"
            interval={0}
            angle={tilt ? -35 : 0}
            textAnchor={tilt ? "end" : "middle"}
            height={tilt ? 84 : 32}
            tickFormatter={shortName}
            tickLine={false}
            axisLine={{ stroke: CHART.grey4 }}
            tick={{ fill: CHART.ink, fontSize: 11 }}
          />
          <YAxis
            domain={[0, 10]}
            ticks={[0, 2, 4, 6, 8, 10]}
            width={32}
            tickLine={false}
            axisLine={false}
            tick={{ fill: CHART.grey1, fontSize: 11 }}
          />
          <Tooltip
            cursor={{ fill: CHART.grey4, fillOpacity: 0.35 }}
            content={<ChartTooltip colours={colours} comments={comments} />}
          />
          {Array.from({ length: slots }, (_, slot) => (
            <Bar
              key={slot}
              dataKey={(row: PackedRow) => row.ratings[slot]}
              radius={[3, 3, 0, 0]}
              maxBarSize={18}
              isAnimationActive={false}
            >
              {rows.map((row) => (
                <Cell key={row.option_id} {...chartCellProps(row, slot, colours)} />
              ))}
            </Bar>
          ))}
          <ReferenceLine y={MIN_STANDARD} stroke={CHART.ink} strokeDasharray="6 4" strokeWidth={1.5} />
        </BarChart>
      </div>
      {answered.some(isOffCatalog) ? (
        <p className="text-xs text-ks-grey-1">* rated but no longer in the item's criteria</p>
      ) : null}
    </div>
  );
}

/** One page section: a bordered card, its title over a divider, so the three sections never run together. */
export function SectionCard({ title, className, children }: { title?: ReactNode; className?: string; children: ReactNode }) {
  return (
    <section className={cn("rounded-lg border border-ks-grey-4 bg-ks-surface p-5 shadow-xs", className)}>
      {title ? <h3 className="mb-4 border-b border-ks-grey-4 pb-3 text-base font-bold">{title}</h3> : null}
      <div className="flex flex-col gap-3">{children}</div>
    </section>
  );
}

const unscored = (row: CriteriaRankingRow) => row.criterion === null;

export const SAMPLE_LIST_COLUMNS: ColumnDef<CriteriaRankingRow, unknown>[] = [
  {
    id: "option",
    header: "Option",
    meta: { sortable: true },
    cell: ({ row }) => (
      <OptionLink optionId={row.original.option_id} itemId={row.original.item_id} from="ranking_table">
        {row.original.option_name}
      </OptionLink>
    ),
  },
  {
    id: "rating",
    header: "Rating",
    meta: { align: "right", sortable: true },
    cell: ({ row }) => {
      if (unscored(row.original)) return null;
      if (row.original.rating == null) return <FlagChip kind="clarify" />;
      return <span className={cn(row.original.rating < MIN_STANDARD && "font-bold text-ks-danger")}>{row.original.rating}</span>;
    },
  },
  {
    id: "criterion",
    header: "Criterion",
    meta: { sortable: true },
    cell: ({ row }) => unscored(row.original) ? (
      <div className="relative h-6">
        <div className="absolute top-0 left-0 flex items-center gap-2 whitespace-nowrap">
          <FlagChip kind="not_scored" /> {FLAGS.not_scored.tooltip}
        </div>
      </div>
    ) : row.original.criterion,
  },
  {
    id: "sample",
    header: "Sample",
    meta: { sortable: true },
    cell: ({ row }) => unscored(row.original) ? null : row.original.sample ?? null,
  },
  {
    id: "evaluator",
    header: "Reviewed by",
    meta: { sortable: true },
    cell: ({ row }) => unscored(row.original) ? null : row.original.evaluator ?? null,
  },
  {
    id: "question",
    header: "Question",
    meta: { sortable: true },
    cell: ({ row }) => unscored(row.original) ? null : <Question text={row.original.question} />,
  },
  {
    id: "comment",
    header: "Comment",
    meta: { sortable: true, className: "whitespace-normal" },
    cell: ({ row }) => unscored(row.original) ? null : row.original.comment,
  },
];

/**
 * v1.6 (Ioan 2 Nov): the Sample and Reviewed by pills narrow the criteria list and the chart. The ranking above
 * never gets them, so the scores stay every reviewer's average. v1.7: Sample sends the picked option + sample keys.
 */
export function recordPicks(search: Record<string, unknown>): ApiParams {
  const out: ApiParams = {};
  const keys = pageFilterList(search, "sample");
  const evaluator = pageFilter(search, "evaluator");
  if (keys.length > 0) out.sample_keys = keys.join(",");
  if (evaluator) out.evaluator = evaluator;
  return out;
}

export function HeatmapSection({ tab, areaName }: { tab: SourcingTab; areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const phase = pagePhase(search);
  return (
    <SectionCard className="mt-6" title={<>{"Criteria by phase \u00b7 "}{phase ?? "All phases"}</>}>
      <TabPage<HeatmapData>
        tab={{ ...tab, apiTab: "criteria-heatmap" }}
        areaName={areaName}
        extra={{ ...(phase ? { criteria_phase: phase } : {}), ...recordPicks(search) }}
        isEmpty={(data) => answeredCriteria(data).length === 0 || data.options.length === 0}
        emptyCopy={Object.keys(recordPicks(search)).length > 0 ? "No records for this sample or reviewer." : "No criteria at this phase yet."}
      >
        {(data) => <CriteriaChart data={pageFilterList(search, "sample").length > 0 ? splitBySample(data) : data} />}
      </TabPage>
    </SectionCard>
  );
}

export function CriteriaListSection({ tab, areaName }: { tab: SourcingTab; areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const phase = pagePhase(search);
  const [page, setPage] = usePage("l");
  const [per, setPer] = usePer("l");
  const [sort, onSort] = useTableSort("l");
  return (
    <SectionCard className="mt-6" title="Criteria list">
      <TabPage<Page<CriteriaRankingRow>>
        tab={{ ...tab, apiTab: "criteria-ranking" }}
        areaName={areaName}
        extra={{
          ...(phase ? { criteria_phase: phase } : {}),
          ...recordPicks(search),
          ...sortParams(sort),
          limit: per,
          offset: (page - 1) * per,
        }}
        isEmpty={(data) => data.total === 0}
      >
        {(data) => (
          <PagedTable<CriteriaRankingRow>
            columns={SAMPLE_LIST_COLUMNS}
            data={data}
            page={page}
            onPage={setPage}
            per={per}
            onPer={setPer}
            caption="Criteria"
            rowClassName={(row) => row.dropped ? MUTED_ROW : undefined}
            sort={sort}
            onSort={onSort}
          />
        )}
      </TabPage>
    </SectionCard>
  );
}
